import React, { useState } from "react";
import { useDispatch } from "react-redux";
import Button from "web/react/components/button/button";
import MultiplyOnHoverButton from "web/react/components/buttons/multiply-on-hover-button/multiply-on-hover-button";
import { CaptureType, openCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";
import { openFollowDesignerModal } from "web/redux/ducks/send-to-app-modal";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import requester from "web/script/modules/requester";

interface FollowDesignerButtonProps {
    lystId: number;
    designerName?: string;
    followedText: string;
    notFollowedText: string;
    isAuthenticated: string;
    isFollowing: string;
    analyticsParameters: [string, string, string];
    className: string;
    downloadApp?: boolean;
}

export function FollowDesignerButton({
    lystId,
    designerName,
    followedText,
    notFollowedText,
    isAuthenticated,
    isFollowing,
    analyticsParameters,
    className,
}: FollowDesignerButtonProps): JSX.Element {
    const [following, setFollowing] = useState<boolean>(isFollowing !== "false");
    const dispatch = useDispatch();

    function follow(): Promise<void> {
        return requester
            .post("/api/users/add/", `lyst_id=${lystId}`)
            .then(() => {
                setFollowing(true);
                analytics.event("Follow Button", "Follow - Success", undefined, true);
            })
            .catch(() => {
                analytics.event("Follow Button", "Follow - Error", undefined, true);
            });
    }

    function unFollow(): Promise<void> {
        return requester
            .post("/api/users/remove/", `lyst_id=${lystId}`)
            .then(() => {
                setFollowing(false);
                analytics.event("Follow Button", "Unfollow - Success", undefined, true);
            })
            .catch(() => {
                analytics.event("Follow Button", "Unfollow - Error", undefined, true);
            });
    }

    function getCaptureType(): CaptureType {
        const pageSubType = environment.get("pageSubType");
        if (["designer", "designer_category", "designer_sub_category"].includes(pageSubType)) {
            return analyticsParameters[0] === "du_feed_banner" ? "signup_du_banner" : "signup_du";
        }
        return "signup_su";
    }

    const buttonLabel = following ? followedText : notFollowedText;

    const [category, action, label] = analyticsParameters;

    function onClick(): void {
        analytics.event(category, action, label);
        const hardBlock = environment.getFeature("mg_show_new_arrivals_rail_pdp");
        if (isAuthenticated === "false") {
            dispatch(
                hardBlock
                    ? openFollowDesignerModal({
                          designerName: designerName as string,
                          isOpen: true,
                      })
                    : openCustomerCaptureOverlay({
                          designerId: lystId.toString(),
                          captureType: getCaptureType(),
                          designerName,
                      })
            );
        } else if (following) {
            unFollow();
        } else {
            follow();
        }
    }

    if (following) {
        return (
            <Button styleType={"primary"} onClick={onClick} className={className}>
                {buttonLabel}
            </Button>
        );
    }
    return (
        <MultiplyOnHoverButton
            className={className}
            onClick={onClick}
            disabled={false}
            fullWidth={false}
            fullHeight={false}
            openInNewTab={false}
        >
            {buttonLabel}
        </MultiplyOnHoverButton>
    );
}

export default FollowDesignerButton;
